import { ReactNode, createContext, useContext } from "react";

import { PackageCartFragmentFragment } from "@/Apollo/schema";
import { ProductUnknown } from "@/common/contracts/product.contracts";
import {
  BoostPackage,
  ListingFeePackage,
} from "@/features/create-ad-packages/contracts/create-ad-packages.contracts";
import { useCreateAdBoostPackages } from "@/features/create-ad-packages/hooks/useCreateAdBoostPackages";
import { useCreateAdListingFeePackages } from "@/features/create-ad-packages/hooks/useCreateAdListingFeePackages";
import { usePackageCartSelection } from "@/features/create-ad-packages/hooks/usePackageCartSelection";

type CreateAdPackagesContextValue = {
  product?: ProductUnknown;
  productId?: string;
  listingFeePackages: ListingFeePackage[];
  boostPackages: BoostPackage[];
  selectedListingFeeId?: string;
  selectedBoostDays: number;
  selectListingFee: (pkg: ListingFeePackage) => void;
  selectBoost: (days: number) => void;
  hasPaidSelection: boolean;
  isMutating: boolean;
  cart?: PackageCartFragmentFragment;
};

const CreateAdPackagesContext =
  createContext<CreateAdPackagesContextValue | null>(null);

type CreateAdPackagesProviderProps = {
  product?: ProductUnknown;
  children: ReactNode;
  /** See `usePackageCartSelection`. Destructive, so opt-in per flow. */
  resetCartOnMount?: boolean;
  /**
   * Price boosts at the create-ad discount. Only the create-ad flow opts in;
   * boosting an existing ad pays the standard price.
   */
  discountedBoost?: boolean;
};

export const CreateAdPackagesProvider = ({
  product,
  children,
  resetCartOnMount,
  discountedBoost,
}: CreateAdPackagesProviderProps) => {
  const { packages: listingFeePackages } =
    useCreateAdListingFeePackages(product);
  const { packages: boostPackages } = useCreateAdBoostPackages(
    product,
    discountedBoost
  );

  const selection = usePackageCartSelection({
    product,
    listingFeePackages,
    resetCartOnMount,
    discountedBoost,
  });

  return (
    <CreateAdPackagesContext.Provider
      value={{
        product,
        productId: product?.id,
        listingFeePackages,
        boostPackages,
        ...selection,
      }}
    >
      {children}
    </CreateAdPackagesContext.Provider>
  );
};

export const useCreateAdPackages = () => {
  const context = useContext(CreateAdPackagesContext);

  if (!context) {
    throw new Error(
      "useCreateAdPackages must be used within a CreateAdPackagesProvider"
    );
  }

  return context;
};
